/**
 * Shared URL search param management for workflow suite views.
 *
 * @module useWorkflowSuiteSearch
 */
import { useCallback } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';

const ROUTE_PATH = '/superadmin/benchmark' as const;

type WorkflowSuiteSearchParams = {
  tab?: string;
  days?: number;
  suiteId?: string;
  /** Selected workflow names to chart. Empty/absent = all workflows. */
  workflows?: string[];
  trigger?: string;
  configId?: string;
  /** Comma-separated run numbers to include in charts (e.g. "1,2,3"). Empty = all. */
  runs?: string;
};

/** Parse the `runs` search param into an array of numbers (empty array = all runs). */
export function parseRunNumbers(runs?: string | number): number[] {
  if (runs === null || runs === undefined) return [];
  // TanStack Router may parse a single-value param as a number
  if (typeof runs === 'number') return [runs];
  return runs
    .split(',')
    .map(Number)
    .filter((n) => !isNaN(n) && n > 0);
}

export function useWorkflowSuiteSearch() {
  const search = useSearch({ from: ROUTE_PATH }) as WorkflowSuiteSearchParams;
  const navigate = useNavigate();

  // Stable across renders (deps: search, navigate) so callers can safely list it
  // in their own useCallback/useMemo dep arrays without re-creating every render.
  const updateSearch = useCallback((updates: Partial<WorkflowSuiteSearchParams>) => {
    const merged = { ...search, ...updates };
    const cleaned: Record<string, unknown> = {};
    if (merged.tab && merged.tab !== 'trends' && merged.tab !== 'workflow-suites')
      cleaned.tab = merged.tab;
    if (merged.days && merged.days !== 30) cleaned.days = merged.days;
    if (merged.suiteId) cleaned.suiteId = merged.suiteId;
    if (merged.workflows && merged.workflows.length > 0) cleaned.workflows = merged.workflows;
    if (merged.trigger) cleaned.trigger = merged.trigger;
    if (merged.configId) cleaned.configId = merged.configId;
    if (merged.runs) cleaned.runs = merged.runs;
    navigate({ to: ROUTE_PATH, search: cleaned, replace: true });
  }, [search, navigate]);

  return { search, updateSearch };
}
